import { useState, useEffect } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Post } from "./news-feed/types";
import { PostCard } from "./news-feed/PostCard";
import { PostDialog } from "./news-feed/PostDialog";
import { useNewsActions } from "./news-feed/useNewsActions";
import { useCarouselAutoplay } from "@/hooks/useCarouselAutoplay";
import { Newspaper, Sparkles } from "lucide-react";

interface NewsFeedProps {
  posts: Post[] | undefined;
}

export const NewsFeed = ({ posts }: NewsFeedProps) => {
  const {
    selectedPost,
    selectedGym,
    hiddenPosts,
    handlePostSelect,
    handleShare,
    togglePostVisibility,
    handleClose
  } = useNewsActions();

  const [api, setApi] = useState<any>(null);
  const [current, setCurrent] = useState(0);
  
  // Usar nuestro hook personalizado para autoplay
  useCarouselAutoplay({ 
    api, 
    delay: 4000, 
    stopOnInteraction: false 
  });

  // Track carousel state
  useEffect(() => {
    if (!api) return;
    
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const visiblePosts = posts?.filter(post => !hiddenPosts.has(post.id));

  return (
    <section className="py-4 sm:py-6 space-y-4 sm:space-y-5 flex-1 animate-fade-in stagger-2">
      {/* Header with icon */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="p-2 rounded-xl bg-primary/10 animate-pulse-glow">
            <Newspaper className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          </div>
          <Sparkles className="h-3 w-3 text-accent absolute -top-1 -right-1 animate-float" />
        </div>
        <h2 className="text-lg sm:text-xl font-heading font-semibold text-primary">
          Últimas Noticias
        </h2>
      </div>
      
      {visiblePosts?.length === 0 ? (
        <div className="glass-card p-6 text-center">
          <p className="text-muted-foreground">
            {posts === undefined 
              ? "Cargando noticias..."
              : "No hay noticias disponibles para tu gimnasio en este momento."}
          </p>
        </div>
      ) : (
        <>
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent>
              {visiblePosts?.map((post, index) => (
                <CarouselItem key={post.id} className="basis-full sm:basis-1/2 lg:basis-1/3">
                  <PostCard post={post} onClick={handlePostSelect} index={index} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Pagination dots */}
          {visiblePosts && visiblePosts.length > 1 && (
            <div className="flex justify-center gap-1.5 pt-2">
              {visiblePosts.map((_, index) => (
                <button
                  key={index}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === current 
                      ? "w-6 bg-primary" 
                      : "w-1.5 bg-primary/30 hover:bg-primary/50"
                  }`}
                  onClick={() => api?.scrollTo(index)}
                  aria-label={`Ir a noticia ${index + 1}`}
                />
              ))}
            </div>
          )}
        </>
      )}

      <PostDialog
        post={selectedPost}
        gym={selectedGym}
        isHidden={selectedPost ? hiddenPosts.has(selectedPost.id) : false}
        onClose={handleClose}
        onShare={handleShare}
        onToggleVisibility={togglePostVisibility}
      />

      <div className="section-divider mt-6" />
    </section>
  );
};
